/**
 * Performance Logger Utility
 * Centralized logging system with performance tracking and conditional output
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  PERFORMANCE = 4,
}

export enum LogCategory {
  GENERAL = 'general',
  CANVAS = 'canvas',
  STATE = 'state',
  API = 'api',
  CSV = 'csv',
  RELATIONSHIP = 'relationship',
  LAYER = 'layer',
  FLOOR = 'floor',
  PERFORMANCE = 'performance',
  RENDERING = 'rendering',
}

interface LogConfig {
  enabled: boolean
  level: LogLevel
  categories: Set<LogCategory>
  measurePerformance: boolean
}

class Logger {
  private config: LogConfig = {
    enabled: process.env.NODE_ENV === 'development', // Only enable in development
    level: LogLevel.INFO,
    categories: new Set(Object.values(LogCategory)),
    measurePerformance: true,
  }

  private performanceMarks: Map<string, number> = new Map()

  /**
   * Configure logger settings
   */
  configure(config: Partial<LogConfig>) {
    this.config = { ...this.config, ...config }
  }

  /**
   * Check if logging is enabled for category and level
   */
  private shouldLog(level: LogLevel, category: LogCategory): boolean {
    return (
      this.config.enabled &&
      level >= this.config.level &&
      this.config.categories.has(category)
    )
  }

  /**
   * Format log message with metadata
   */
  private format(
    level: LogLevel,
    category: LogCategory,
    message: string,
    _data?: any
  ): string {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0]
    const levelStr = LogLevel[level]
    return `[${timestamp}] [${levelStr}] [${category}] ${message}`
  }

  /**
   * Debug level logging
   */
  debug(category: LogCategory, message: string, data?: any) {
    if (!this.shouldLog(LogLevel.DEBUG, category)) return

    const formatted = this.format(LogLevel.DEBUG, category, message, data)
    console.debug(formatted, data || '')
  }

  /**
   * Info level logging
   */
  info(category: LogCategory, message: string, data?: any) {
    if (!this.shouldLog(LogLevel.INFO, category)) return

    const formatted = this.format(LogLevel.INFO, category, message, data)
    console.log(formatted, data || '')
  }

  /**
   * Warning level logging
   */
  warn(category: LogCategory, message: string, data?: any) {
    if (!this.shouldLog(LogLevel.WARN, category)) return

    const formatted = this.format(LogLevel.WARN, category, message, data)
    console.warn(formatted, data || '')
  }

  /**
   * Error level logging
   */
  error(category: LogCategory, message: string, error?: Error | any) {
    if (!this.shouldLog(LogLevel.ERROR, category)) return

    const formatted = this.format(LogLevel.ERROR, category, message, error)
    console.error(formatted, error || '')
  }

  /**
   * Performance measurement - start
   */
  startMeasure(name: string) {
    if (!this.config.measurePerformance) return

    this.performanceMarks.set(name, performance.now())
  }

  /**
   * Performance measurement - end
   */
  endMeasure(name: string, threshold?: number) {
    if (!this.config.measurePerformance) return

    const start = this.performanceMarks.get(name)
    if (!start) {
      this.warn(LogCategory.PERFORMANCE, `No start mark for: ${name}`)
      return
    }

    const duration = performance.now() - start
    this.performanceMarks.delete(name)

    // Only log if threshold is exceeded (or no threshold set)
    if (!threshold || duration > threshold) {
      this.info(
        LogCategory.PERFORMANCE,
        `${name} completed in ${duration.toFixed(2)}ms`
      )
    }

    return duration
  }

  /**
   * Performance measurement - async wrapper
   */
  async measureAsync<T>(
    name: string,
    fn: () => Promise<T>,
    threshold?: number
  ): Promise<T> {
    this.startMeasure(name)
    try {
      const result = await fn()
      this.endMeasure(name, threshold)
      return result
    } catch (error) {
      this.endMeasure(name)
      this.error(LogCategory.PERFORMANCE, `${name} failed`, error)
      throw error
    }
  }

  /**
   * Performance measurement - sync wrapper
   */
  measure<T>(name: string, fn: () => T, threshold?: number): T {
    this.startMeasure(name)
    try {
      const result = fn()
      this.endMeasure(name, threshold)
      return result
    } catch (error) {
      this.endMeasure(name)
      this.error(LogCategory.PERFORMANCE, `${name} failed`, error)
      throw error
    }
  }

  /**
   * Group logging
   */
  group(category: LogCategory, label: string) {
    if (!this.shouldLog(LogLevel.INFO, category)) return
    console.group(`[${category}] ${label}`)
  }

  groupEnd() {
    console.groupEnd()
  }

  /**
   * Table logging for structured data
   */
  table(category: LogCategory, data: any) {
    if (!this.shouldLog(LogLevel.INFO, category)) return
    console.table(data)
  }
}

// Singleton instance
export const logger = new Logger()

// Convenience exports
export const log = {
  debug: (category: LogCategory, message: string, data?: any) =>
    logger.debug(category, message, data),
  info: (category: LogCategory, message: string, data?: any) =>
    logger.info(category, message, data),
  warn: (category: LogCategory, message: string, data?: any) =>
    logger.warn(category, message, data),
  error: (category: LogCategory, message: string, error?: Error | any) =>
    logger.error(category, message, error),
  perf: {
    start: (name: string) => logger.startMeasure(name),
    end: (name: string, threshold?: number) => logger.endMeasure(name, threshold),
    measure: <T,>(name: string, fn: () => T, threshold?: number) =>
      logger.measure(name, fn, threshold),
    measureAsync: <T,>(name: string, fn: () => Promise<T>, threshold?: number) =>
      logger.measureAsync(name, fn, threshold),
  },
  group: (category: LogCategory, label: string) => logger.group(category, label),
  groupEnd: () => logger.groupEnd(),
  table: (category: LogCategory, data: any) => logger.table(category, data),
}

// Configure based on environment
if (process.env.NODE_ENV === 'production') {
  logger.configure({
    enabled: false, // Disable all logging in production
  })
}
